import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { mockAdminCustomers } from '@/mocks/adminCustomers';

const roleFilters = ['all', 'customer', 'admin', 'super_admin', 'factory_manager', 'sales_manager'] as const;

type RoleFilter = typeof roleFilters[number];

const roleBadgeStyles: Record<string, string> = {
  customer: 'bg-stone-100 text-stone-700 border-stone-200',
  admin: 'bg-amber-50 text-amber-700 border-amber-200',
  super_admin: 'bg-red-50 text-red-700 border-red-200',
  factory_manager: 'bg-blue-50 text-blue-700 border-blue-200',
  sales_manager: 'bg-green-50 text-green-700 border-green-200',
};

const roleLabels: Record<string, string> = {
  all: 'All',
  customer: 'Customer',
  admin: 'Admin',
  super_admin: 'Super Admin',
  factory_manager: 'Factory Manager',
  sales_manager: 'Sales Manager',
};

export default function AdminCustomersPage() {
  const { t } = useTranslation(['admin', 'auth']);
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');

  // In production, fetch from Supabase. For now use mock data
  const customers = mockAdminCustomers;

  const filteredCustomers = useMemo(() => {
    return customers.filter((c) => {
      const matchesSearch =
        !search ||
        c.full_name.toLowerCase().includes(search.toLowerCase()) ||
        c.email.toLowerCase().includes(search.toLowerCase()) ||
        (c.company_name && c.company_name.toLowerCase().includes(search.toLowerCase()));

      const matchesRole = roleFilter === 'all' || c.role === roleFilter;

      return matchesSearch && matchesRole;
    });
  }, [customers, search, roleFilter]);

  return (
    <div>
      {/* Filters */}
      <div className="px-4 md:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <p className="text-sm text-stone-500">{t('admin.customers.subtitle')}</p>
          <span className="text-sm text-stone-500 whitespace-nowrap">
            {t('admin.customers.total', { count: filteredCustomers.length })}
          </span>
        </div>

        <div className="bg-white rounded-xl border border-stone-200 p-4 shadow-sm">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('admin.customers.search')}
                className="w-full pl-9 pr-4 py-2 text-sm border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-colors"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {roleFilters.map((role) => (
                <button
                  key={role}
                  onClick={() => setRoleFilter(role)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors whitespace-nowrap ${
                    roleFilter === role
                      ? 'bg-amber-600 text-white border-amber-600'
                      : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-50'
                  }`}
                >
                  {roleLabels[role]}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="px-4 md:px-6 lg:px-8 pb-10">
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-stone-50 border-b border-stone-200">
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                    {t('admin.customers.columns.name')}
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                    {t('admin.customers.columns.email')}
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                    {t('admin.customers.columns.company')}
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                    {t('admin.customers.columns.country')}
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                    {t('admin.customers.columns.role')}
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                    {t('admin.customers.columns.joined')}
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                    {t('admin.customers.columns.status')}
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider text-right">
                    {t('admin.customers.columns.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filteredCustomers.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-sm text-stone-400">
                      {t('admin.customers.noResults')}
                    </td>
                  </tr>
                ) : (
                  filteredCustomers.map((customer) => (
                    <tr
                      key={customer.id}
                      className="hover:bg-stone-50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {customer.avatar_url ? (
                            <img
                              src={customer.avatar_url}
                              alt={customer.full_name}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-xs font-semibold text-amber-700">
                              {customer.full_name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <span className="text-sm font-medium text-stone-900 whitespace-nowrap">
                            {customer.full_name}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-stone-600 whitespace-nowrap">
                        {customer.email}
                      </td>
                      <td className="px-4 py-3 text-sm text-stone-600 whitespace-nowrap">
                        {customer.company_name || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-stone-600 whitespace-nowrap">
                        {customer.country || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border whitespace-nowrap ${
                            roleBadgeStyles[customer.role] || 'bg-stone-100 text-stone-700 border-stone-200'
                          }`}
                        >
                          {roleLabels[customer.role] || customer.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-stone-500 whitespace-nowrap">
                        {new Date(customer.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${
                            customer.status === 'active'
                              ? 'bg-green-50 text-green-700 border border-green-200'
                              : 'bg-stone-100 text-stone-600 border border-stone-200'
                          }`}
                        >
                          {customer.status === 'active'
                            ? t('admin.customers.status.active')
                            : t('admin.customers.status.inactive')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => navigate(`/admin/customers/${customer.id}`)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors whitespace-nowrap"
                        >
                          {t('admin.customers.viewDetail')}
                          <i className="ri-arrow-right-line" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}